/**
 * Supabase Auth reports a refused sign-in (provider error, not invited, expired link) by redirecting back with
 * `error` / `error_description` in the query string or the hash (issue #6). Read it, explain it in plain words, and
 * remove it from the address so a reload does not show it again. Other parameters (such as `?tenant=`) are kept.
 */
const KEYS = ['error', 'error_code', 'error_description'];

export function readAuthError(loc: Pick<Location, 'search' | 'hash'> = window.location): string | null {
  const q = new URLSearchParams(loc.search);
  const h = new URLSearchParams(loc.hash.replace(/^#/, ''));
  const desc = q.get('error_description') ?? h.get('error_description');
  const code = q.get('error') ?? h.get('error') ?? q.get('error_code') ?? h.get('error_code');
  if (!desc && !code) return null;
  return explain(desc ?? code ?? '');
}

export function explain(raw: string): string {
  const d = raw.trim();
  if (/email/i.test(d) && /(provider|external|claim)/i.test(d)) {
    return 'Microsoft or Google did not give Teslyn an email address for this account, so it cannot be matched to an invitation. Ask your administrator to check that the account has an email address, then try again.';
  }
  if (/signup|sign-up|not allowed|not invited|user not found/i.test(d)) {
    return 'This account has not been invited. Ask your administrator to send an invitation to the email address you use with Microsoft or Google.';
  }
  if (/expired|invalid.*(link|token|otp)|otp/i.test(d)) {
    return 'This sign-in link has expired or was already used. Request a new link below.';
  }
  if (/access_denied|cancel/i.test(d)) {
    return 'Sign-in was cancelled. Try again when you are ready.';
  }
  return `Sign-in did not complete: ${d}`;
}

/** Remove the error parameters from the address bar, keeping everything else. */
export function clearAuthError(win: Pick<Window, 'location' | 'history'> = window): void {
  const url = new URL(win.location.href);
  let changed = false;
  for (const k of KEYS) {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      changed = true;
    }
  }
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  if (KEYS.some((k) => hash.has(k))) {
    url.hash = '';
    changed = true;
  }
  if (changed) win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
}
